import { useGetBusinessProfile } from '@/apis/business/hook';
import {
  useGetBusinessBillingHistories,
  useFetchBusinessInvoice,
  useMarkBillingStatusPaid,
  useMarkBillingStatusVoid
} from '@/apis/business/setting/billing/hook';
import { BillingHistoryResponse } from '@/apis/business/setting/billing/types';
import { useGetCurrentUser } from '@/apis/user/hook';
import Button from '@/components/Common/Button';
import { formatDollars } from '@/utils/helperFunctions';
import {
  Box,
  Typography,
  Chip,
  useTheme,
  IconButton,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { ColumnDef } from '@tanstack/react-table';
import DataTable from 'components/Common/DataTable';
import { useMemo, useState } from 'react';
import { MdOutlineFileDownload, MdKeyboardArrowDown, MdKeyboardArrowUp } from 'react-icons/md';
import BillingHistoryRowDetails from '@/components/Common/BillingHistoryRowDetails';

export default function BillingHistory() {
  const { data: BillingHistoryData, isLoading } = useGetBusinessBillingHistories();
  const { data: businessProfile, isLoading: loadingBusinessDetails } = useGetBusinessProfile();
  const { data: user, isLoading: loadingUserDetails } = useGetCurrentUser();
  const isStatusEditable = user?.role === 'agency_admin' && !businessProfile?.agency_billing_enabled;
  const memoizedBillingHistory = useMemo(() => BillingHistoryData?.data ?? [], [BillingHistoryData?.data]);
  const { mutateAsync: markStatusPaid } = useMarkBillingStatusPaid();
  const { mutateAsync: markStatusVoid } = useMarkBillingStatusVoid();
  const {
    mutate: fetchInvoice,
    isPending: isFetchingInvoice,
    variables: fetchingInvoiceId
  } = useFetchBusinessInvoice();

  return (
    <Box>
      <Typography variant='h6'>Billing History</Typography>
      <Typography
        variant='body2'
        color='text.secondary'
        mb={2}
        gutterBottom
        fontSize={16}
        mt={0.5}
        sx={{ display: { xs: 'none', sm: 'block' } }}
      >
        Monthly usage & billing details
      </Typography>
      <DataTable
        isLoading={isLoading || loadingBusinessDetails || loadingUserDetails}
        data={memoizedBillingHistory}
        columns={columns(
          isStatusEditable,
          markStatusPaid,
          markStatusVoid,
          fetchInvoice,
          isFetchingInvoice,
          fetchingInvoiceId
        )}
        renderAfterRow={(row) => <BillingHistoryRowDetails invoiceId={row.original.id} />}
      />
    </Box>
  );
}

export const columns = (
  isStatusEditable: boolean,
  markStatusPaid: (id: number) => void,
  markStatusVoid: (id: number) => void,
  fetchInvoice: (id: number) => void,
  isFetchingInvoice: boolean,
  fetchingInvoiceId?: number
): ColumnDef<BillingHistoryResponse>[] => {
  return [
    {
      accessorKey: 'month',
      header: 'Month',
      cell: ({ row }) => (
        <Box display='flex' alignItems='center' gap={0.5}>
          <IconButton size='small' onClick={row.getToggleExpandedHandler()} sx={{ p: 0.25 }}>
            {row.getIsExpanded() ? <MdKeyboardArrowUp size={18} /> : <MdKeyboardArrowDown size={18} />}
          </IconButton>
          <Typography>{row.original.month}</Typography>
        </Box>
      )
    },

    {
      accessorKey: 'subtotal',
      header: 'Subtotal',
      cell: (info) => formatDollars(info?.row?.original?.subtotal)
    },
    {
      accessorKey: 'tax_amount',
      header: 'Tax',
      cell: (info) => formatDollars(info?.row?.original?.tax_amount)
    },
    {
      accessorKey: 'discount_amount',
      header: 'Discount',
      cell: (info) => (
        <Typography fontWeight={500} color='#2E7D32'>
          {formatDollars(info?.row?.original?.discount_amount)}
        </Typography>
      )
    },
    {
      accessorKey: 'total',
      header: 'Total(Incl. Tax)',
      size: 180,
      cell: (info) => formatDollars(info?.row?.original?.total)
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <RenderStatus
          id={row?.original?.id}
          isStatusEditable={isStatusEditable}
          status={row?.original?.status}
          markPaid={markStatusPaid}
          markVoid={markStatusVoid}
        />
      )
    },
    {
      id: 'invoice',
      header: 'Invoice',
      cell: (info) => {
        const { id, status } = info.row.original;
        const isRowFetching = isFetchingInvoice && fetchingInvoiceId === id;
        return ['finalized', 'failed', 'voided', 'succeeded', 'refunded'].includes(status) ? (
          <IconButton color='primary' disabled={!id || isRowFetching} onClick={() => id && fetchInvoice(id)}>
            <MdOutlineFileDownload />
          </IconButton>
        ) : (
          ''
        );
      }
    }
  ];
};

const RenderStatus = ({
  id,
  status,
  markPaid,
  markVoid,
  isStatusEditable
}: {
  id: number;
  status: string;
  isStatusEditable: boolean;
  markPaid: (id: number) => void;
  markVoid: (id: number) => void;
}) => {
  const theme = useTheme();

  const [openConfirm, setOpenConfirm] = useState(false);
  const [selectedAction, setSelectedAction] = useState<'Paid' | 'Voided' | null>(null);

  const handleStatusUpdate = (updatedStatus: string) => {
    if (updatedStatus === 'Paid' || updatedStatus === 'Voided') {
      setSelectedAction(updatedStatus as 'Paid' | 'Voided');
      setOpenConfirm(true);
    }
  };

  const handleConfirm = () => {
    if (selectedAction === 'Paid') markPaid(id);
    if (selectedAction === 'Voided') markVoid(id);

    setOpenConfirm(false);
    setSelectedAction(null);
  };

  const statusConfig = useMemo(() => {
    switch (status) {
      case 'pending':
      case 'draft':
        return { color: '#777777', bgcolor: '#ECECEC', label: 'Draft' };
      case 'finalized':
      case 'failed':
        return { color: '#7C00B1', bgcolor: '#F5DFFF', label: 'Open' };
      case 'succeeded':
        return { color: '#106520ff', bgcolor: '#bae7ccff', label: 'Paid' };
      case 'voided':
        return { color: '#FB8703', bgcolor: '#FFF3E5', label: 'Voided' };
      case 'refunded':
        return { color: '#B36B00', bgcolor: '#FFF3DF', label: 'Refunded' };
      default:
        return {
          color: theme.palette.primary.dark,
          bgcolor: theme.palette.primary.light,
          label: status
        };
    }
  }, [status, theme]);

  if (['finalized', 'failed'].includes(status) && isStatusEditable) {
    return (
      <>
        <Select value={statusConfig.label} onChange={(e) => handleStatusUpdate(e.target.value)} sx={{ height: '38px' }}>
          <MenuItem value='Open'>Open</MenuItem>
          <MenuItem value='Voided'>Void</MenuItem>
          <MenuItem value='Paid'>Paid</MenuItem>
        </Select>

        {/* Confirm Dialog */}
        <Dialog
          open={openConfirm}
          onClose={() => setOpenConfirm(false)}
          PaperProps={{
            sx: {
              borderRadius: 1,
              px: 2,
              py: 1.5,
              textAlign: 'center'
            }
          }}
        >
          <DialogTitle sx={{ fontWeight: 500, textAlign: 'center' }}>Are you sure?</DialogTitle>

          <DialogContent>
            <Typography sx={{ textAlign: 'center', mt: 0.5 }}>
              {selectedAction === 'Paid'
                ? 'This invoice will be marked as paid. This action is permanent and cannot be undone.'
                : 'This invoice will be permanently voided and can no longer be edited or restored.'}
            </Typography>
          </DialogContent>

          <DialogActions
            sx={{
              justifyContent: 'center',
              gap: 1.5,
              pb: 2
            }}
          >
            <Button
              onClick={() => setOpenConfirm(false)}
              variant='outlined'
              sx={{ border: '1px solid #777', color: '#777', px: 2, minHeight: '38px' }}
            >
              Cancel
            </Button>

            <Button onClick={handleConfirm} variant='contained' sx={{ minHeight: '38px' }}>
              {selectedAction === 'Paid' ? 'Mark as Paid' : 'Void Invoice'}
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  return (
    <Chip
      label={statusConfig.label}
      size='small'
      sx={{
        color: statusConfig.color,
        bgcolor: statusConfig.bgcolor,
        p: 2,
        borderRadius: 6,
        fontWeight: 500
      }}
    />
  );
};
